import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { HandCoins, Users, Calendar, ScanLine, ArrowUpRight, Quote } from "lucide-react";

export default function Impact() {
  const [impact, setImpact] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    api.get("/impact").then((r) => setImpact(r.data));
    api.get("/impact/detailed").then((r) => setBreakdown(r.data));
    api.get("/quotes").then((r) => setQuotes(r.data));
  }, []);

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-10">
        <div className="eyebrow">Transparency</div>
        <h1 className="font-display text-4xl sm:text-6xl font-extrabold mt-3 max-w-3xl leading-[0.95]">Numbers we can point at, not stories we made up.</h1>
        <p className="mt-5 text-lg text-stone-600 dark:text-stone-400 max-w-2xl">Every figure below is generated live from real bookings, real check-ins and real refunds. We would rather be small and honest than large and imagined.</p>
      </section>

      {impact && (
        <section className="border-y border-border bg-[color:var(--sand)] dark:bg-stone-900/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            <BigStat icon={HandCoins} value={fmtINR(impact.funds_raised_paise)} label="Raised across all events" tid="imp-funds" />
            <BigStat icon={Calendar} value={impact.events_hosted + impact.events_upcoming} label="Programs on the calendar" tid="imp-events" />
            <BigStat icon={Users} value={impact.unique_supporters} label="Unique supporters" tid="imp-supporters" />
            <BigStat icon={ScanLine} value={impact.check_ins} label="Verified check-ins" tid="imp-checkins" />
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="eyebrow">Per event</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Where the numbers come from</h2>
        <div className="mt-8 space-y-3">
          {breakdown.map((e) => (
            <Link key={e.id} to={`/events/${e.slug}`} data-testid={`impact-event-${e.slug}`} className="cgc-card p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center hover:-translate-y-0.5 transition-transform">
              <div className="md:col-span-2">
                <img src={e.cover_image} alt="" className="w-full h-20 md:h-16 object-cover rounded-md" />
              </div>
              <div className="md:col-span-5">
                <div className="text-xs font-mono uppercase tracking-widest text-stone-500">{e.category} · {e.city} · {e.past ? "Past" : "Upcoming"}</div>
                <div className="font-display text-lg font-semibold mt-1">{e.title}</div>
              </div>
              <MiniStat label="Bookings" value={e.bookings} />
              <MiniStat label="Attendees" value={e.attendees} />
              <MiniStat label="Raised" value={fmtINR(e.revenue_paise)} />
              <div className="hidden md:flex md:col-span-1 justify-end"><ArrowUpRight size={18} className="text-stone-400" /></div>
            </Link>
          ))}
        </div>
      </section>

      {quotes.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="eyebrow">Voices</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">People we work alongside</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {quotes.map((q) => (
              <blockquote key={q.id} data-testid={`quote-${q.id}`} className="cgc-card p-6 border-l-4 border-[color:var(--brand-terracotta)]">
                <Quote className="text-[color:var(--brand-terracotta)]" />
                <p className="text-lg font-display italic mt-3">&ldquo;{q.quote}&rdquo;</p>
                <footer className="mt-4 text-sm">
                  <div className="font-semibold">{q.author}</div>
                  {q.role && <div className="text-xs text-stone-500">{q.role}</div>}
                </footer>
              </blockquote>
            ))}
          </div>
        </section>
      )}
    </PublicLayout>
  );
}

function BigStat({ icon: Icon, value, label, tid }) {
  return (
    <div data-testid={tid}>
      <div className="w-11 h-11 rounded-md bg-[color:var(--brand-terracotta)]/10 text-[color:var(--brand-terracotta)] flex items-center justify-center mb-3"><Icon size={20} /></div>
      <div className="font-display text-3xl sm:text-4xl font-extrabold leading-none">{value}</div>
      <div className="text-xs text-stone-500 mt-2">{label}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="md:col-span-1">
      <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">{label}</div>
      <div className="font-display text-lg font-semibold">{value}</div>
    </div>
  );
}
